import { apiClient } from '@/services/apiClient';
import type {
  CdmOrder,
  CstOrder,
  PowerBiAccount,
  ReportAudienceCatalog,
  ReportFamily,
  ReportFilterCatalog,
  ReportPreset,
  ReportResult,
  ReportResultType,
  ReportRunResponse,
  ReportSubFilterCatalog,
  ResultDeliverResponse,
  ScheduledRunNowResponse,
} from '@/types/customReports.types';

/**
 * Client for the v5 custom-report API (/v5/reports/*).
 *
 * All endpoints are documented in API_V5_DOCUMENTATION.md Section 31 and
 * match the ReportsOnDemandController, ReportsScheduledController,
 * ReportResultsController, ReportPresetsController, ReportCatalogController,
 * and ReportPowerBiController on the API side.
 */
/**
 * A row in the unified Reports list (advanced side): one per report order,
 * enriched with the creator name and the id of its latest generated result.
 * Backed by ReportResultsController::overview → GET /reports/overview.
 */
export interface AdvancedReportRow {
  kind: 'advanced';
  type: ReportResultType; // 'cdm' | 'cst'
  orderId: number;
  title: string;
  reportSelect: string | null;
  scheduled: boolean;
  createdAt: string | null;
  generatedBy: string | null;
  latestResultId: number | null;
}

/**
 * The server's `error.message` from a refused blob request, or null when the
 * body is not JSON. Mirrors attachmentErrorMessage in v5/services/notes.service.ts.
 * Only `error.message`, the shape errorResponse writes on purpose: Laravel's
 * default 500 body is a bare `message` ("Server Error", or the exception text
 * under APP_DEBUG), and that must fall through to getErrorMessage's fallback.
 */
async function blobErrorMessage(err: unknown): Promise<string | null> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const json = JSON.parse(await data.text());
    const msg = json?.error?.message;
    return msg ? String(msg) : null;
  } catch {
    return null;
  }
}

export const customReportsApi = {
  // ── Unified advanced-report list (for the Reports page) ───────────────────
  overview: async (): Promise<AdvancedReportRow[]> => {
    const { data } = await apiClient.get<{ data: AdvancedReportRow[] }>('/reports/overview');
    return data.data ?? [];
  },

  // ── Catalog ──────────────────────────────────────────────────────────────
  catalog: {
    families: async (): Promise<ReportFamily[]> => {
      const { data } = await apiClient.get<{ data: ReportFamily[] }>(
        '/reports/catalog/families',
      );
      return data.data ?? [];
    },
    filters: async (family: string): Promise<ReportFilterCatalog> => {
      const { data } = await apiClient.get<{ data: ReportFilterCatalog }>(
        '/reports/catalog/filters',
        { params: { family } },
      );
      return data.data;
    },
    audience: async (): Promise<ReportAudienceCatalog> => {
      const { data } = await apiClient.get<{ data: ReportAudienceCatalog }>(
        '/reports/catalog/audience',
      );
      return data.data;
    },
    /**
     * Choice lists for every legacy sub-filter dimension (coordinators,
     * facilities, stages, …). One round-trip per family — drives the
     * "Filters" panel on the wizard's Filters step.
     */
    subFilters: async (family: string): Promise<ReportSubFilterCatalog> => {
      const { data } = await apiClient.get<{ data: ReportSubFilterCatalog }>(
        '/reports/catalog/sub-filters',
        { params: { family } },
      );
      return data.data;
    },
  },

  // ── On-demand (CDM) ──────────────────────────────────────────────────────
  onDemand: {
    list: async (): Promise<CdmOrder[]> => {
      const { data } = await apiClient.get<{ data: CdmOrder[] }>('/reports/builder');
      return data.data ?? [];
    },
    get: async (id: number): Promise<CdmOrder> => {
      const { data } = await apiClient.get<{ data: CdmOrder }>(`/reports/builder/${id}`);
      return data.data;
    },
    create: async (body: Partial<CdmOrder>): Promise<CdmOrder> => {
      const { data } = await apiClient.post<{ data: CdmOrder }>('/reports/builder', body);
      return data.data;
    },
    update: async (id: number, body: Partial<CdmOrder>): Promise<CdmOrder> => {
      const { data } = await apiClient.put<{ data: CdmOrder }>(`/reports/builder/${id}`, body);
      return data.data;
    },
    remove: async (id: number): Promise<void> => {
      await apiClient.delete(`/reports/builder/${id}`);
    },
    /**
     * Synchronously generate + persist + deliver. The API returns the chart
     * payload (for immediate inline render) and the delivery outcome
     * (result id, XLSX paths, email recipients). See
     * api/app/Http/Controllers/V5/ReportsOnDemandController::run.
     */
    run: async (id: number, body?: { force?: boolean }): Promise<ReportRunResponse> => {
      const { data } = await apiClient.post<{ data: ReportRunResponse }>(
        `/reports/builder/${id}/run`,
        body ?? {},
      );
      return data.data;
    },
  },

  // ── Scheduled (CST) ──────────────────────────────────────────────────────
  scheduled: {
    list: async (): Promise<CstOrder[]> => {
      const { data } = await apiClient.get<{ data: CstOrder[] }>('/reports/scheduled');
      return data.data ?? [];
    },
    get: async (id: number): Promise<CstOrder> => {
      const { data } = await apiClient.get<{ data: CstOrder }>(`/reports/scheduled/${id}`);
      return data.data;
    },
    create: async (body: Partial<CstOrder>): Promise<CstOrder> => {
      const { data } = await apiClient.post<{ data: CstOrder }>('/reports/scheduled', body);
      return data.data;
    },
    update: async (id: number, body: Partial<CstOrder>): Promise<CstOrder> => {
      const { data } = await apiClient.put<{ data: CstOrder }>(`/reports/scheduled/${id}`, body);
      return data.data;
    },
    remove: async (id: number): Promise<void> => {
      await apiClient.delete(`/reports/scheduled/${id}`);
    },
    pause: async (id: number): Promise<{ reportsCustomOrdersID: number; active: number }> => {
      const { data } = await apiClient.post<{ data: { reportsCustomOrdersID: number; active: number } }>(
        `/reports/scheduled/${id}/pause`,
      );
      return data.data;
    },
    resume: async (id: number): Promise<{ reportsCustomOrdersID: number; active: number }> => {
      const { data } = await apiClient.post<{ data: { reportsCustomOrdersID: number; active: number } }>(
        `/reports/scheduled/${id}/resume`,
      );
      return data.data;
    },
    /**
     * Enqueues the scheduled order into `reports_custom_orders_quick` so the
     * legacy autorun cron (and the eventual v5 artisan runner) pick it up on
     * its next tick. Returns the queue row id.
     */
    runNow: async (id: number): Promise<ScheduledRunNowResponse> => {
      const { data } = await apiClient.post<{ data: ScheduledRunNowResponse }>(
        `/reports/scheduled/${id}/run-now`,
      );
      return data.data;
    },
  },

  // ── Results ──────────────────────────────────────────────────────────────
  results: {
    /**
     * Lists results scoped to the calling user. Matches the legacy
     * `getReportsListDashboard` OR-clause (rco.userID OR role match OR
     * userlevelN match OR rcru.userID via reports_custom_results_users).
     *
     * Optional filters all map to query params on /reports/results:
     *   - orderId      : single order's runs
     *   - groupByOrder : return latest run per order only (legacy
     *                    `MAX(reportsCustomResultsID) GROUP BY ...`)
     *   - dateFrom/dateTo : inclusive YYYY-MM-DD bounds on dateCreated
     *   - reportSelect : single key or array of report family keys to
     *                    filter on the originating order's reportSelect
     */
    list: async (params?: {
      orderId?: number;
      type?: ReportResultType;
      groupByOrder?: boolean;
      dateFrom?: string;
      dateTo?: string;
      reportSelect?: string | string[];
    }): Promise<ReportResult[]> => {
      const { data } = await apiClient.get<{ data: ReportResult[] }>('/reports/results', { params });
      return data.data ?? [];
    },
    get: async (id: number, type: ReportResultType = 'cdm'): Promise<ReportResult> => {
      const { data } = await apiClient.get<{ data: ReportResult }>(`/reports/results/${id}`, {
        params: { type },
      });
      return data.data;
    },
    /**
     * Fetches the stored XLSX. Format is always xlsx — the stored fileName is
     * authoritative (the API doesn't re-generate on download). PDF is
     * deferred (Phase 5 scope).
     *
     * A file over the Lambda response cap comes back as a JSON
     * `{ redirect }` to a signed S3 URL instead of bytes, so this answers
     * either `{ blob }` or `{ url }`. Because the request asks for a blob, a
     * refusal's JSON body arrives as a Blob too; its message is read out and
     * thrown so the viewer can show it.
     */
    download: async (
      id: number,
      params: { type: ReportResultType },
    ): Promise<{ blob: Blob } | { url: string }> => {
      let blob: Blob;
      try {
        const resp = await apiClient.get(`/reports/results/${id}/download`, {
          params,
          responseType: 'blob',
        });
        blob = resp.data as Blob;
      } catch (err) {
        const message = await blobErrorMessage(err);
        if (message) throw new Error(message);
        throw err;
      }

      if (blob.type.startsWith('application/json')) {
        const json = JSON.parse(await blob.text()) as { redirect?: string };
        if (json.redirect) return { url: json.redirect };
      }
      return { blob };
    },
    /**
     * Re-sends the stored result's XLSX via Mandrill. Recipients default to
     * the originating order's `emailAddresses` column unless overridden here
     * (comma-separated string; matches legacy wire format).
     */
    deliver: async (
      id: number,
      body?: { emailAddresses?: string; type?: ReportResultType },
    ): Promise<ResultDeliverResponse> => {
      const { data } = await apiClient.post<{ data: ResultDeliverResponse }>(
        `/reports/results/${id}/deliver`,
        body ?? {},
      );
      return data.data;
    },
  },

  // ── Presets ──────────────────────────────────────────────────────────────
  presets: {
    list: async (): Promise<ReportPreset[]> => {
      const { data } = await apiClient.get<{ data: ReportPreset[] }>('/reports/presets');
      return data.data ?? [];
    },
    get: async (id: number): Promise<ReportPreset> => {
      const { data } = await apiClient.get<{ data: ReportPreset }>(`/reports/presets/${id}`);
      return data.data;
    },
    create: async (body: Partial<ReportPreset>): Promise<ReportPreset> => {
      const { data } = await apiClient.post<{ data: ReportPreset }>('/reports/presets', body);
      return data.data;
    },
    update: async (id: number, body: Partial<ReportPreset>): Promise<ReportPreset> => {
      const { data } = await apiClient.put<{ data: ReportPreset }>(`/reports/presets/${id}`, body);
      return data.data;
    },
    remove: async (id: number): Promise<void> => {
      await apiClient.delete(`/reports/presets/${id}`);
    },
    cloneToOrder: async (
      id: number,
      body: { target: 'builder' | 'scheduled' },
    ): Promise<{ orderId: number; target: 'builder' | 'scheduled' }> => {
      const { data } = await apiClient.post<{ data: { orderId: number; target: 'builder' | 'scheduled' } }>(
        `/reports/presets/${id}/clone-to-order`,
        body,
      );
      return data.data;
    },
  },

  // ── Power BI account ─────────────────────────────────────────────────────
  powerBi: {
    get: async (): Promise<PowerBiAccount | null> => {
      const { data } = await apiClient.get<{ data: PowerBiAccount | null }>(
        '/reports/power-bi/account',
      );
      return data.data;
    },
    update: async (body: Partial<PowerBiAccount> & {
      title?: string;
      client_id?: string;
      client_secret?: string;
      username?: string;
      password?: string;
      group?: string;
      endDate?: string;
      pOrder?: string | number;
    }): Promise<PowerBiAccount> => {
      const { data } = await apiClient.put<{ data: PowerBiAccount }>('/reports/power-bi/account', body);
      return data.data;
    },
    test: async (): Promise<{ configured: boolean; tokenAcquired: boolean; message?: string }> => {
      const { data } = await apiClient.post<{ data: { configured: boolean; tokenAcquired: boolean; message?: string } }>(
        '/reports/power-bi/account/test',
      );
      return data.data;
    },
  },
};
